import React from 'react';
import { useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { H3, LayoutSingleColumn, NamedLink, Page } from '../../components';
import SectionFooter from '../PageBuilder/SectionBuilder/SectionFooter';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import css from './PressRoomPage.module.css';

const PressRoomPage = props => {
    const config = useConfiguration();
    const intl = useIntl();

    const marketplaceName = config.marketplaceName;
    const schemaTitle = intl.formatMessage({ id: 'PressRoomPage.schemaTitle' }, { marketplaceName });
    const schemaDescription = intl.formatMessage({ id: 'PressRoomPage.schemaDescription' }, { marketplaceName });
    return (
        <Page
            title={schemaTitle}
            description={schemaDescription}
        >
            <LayoutSingleColumn
                topbar={<TopbarContainer />}
                footer={<SectionFooter />}
                mainColumnClassName={css.mainColumnClassName}
            >
                <div className={css.root}>
                    <H3>
                        Press Centre
                    </H3>
                    <p className={css.info}>
                        Welcome to the HouseSit Match Press Centre. This page serves as a central hub for all media-related information, including recent press coverage, company background, awards, and events. If you're a journalist or media representative, please feel free to contact us for further information or inquiries.
                    </p>
                    <p className={css.subtitle}>
                        Recent Media Coverage
                    </p>
                    <p className={css.info}>
                        HouseSit Match has been featured in various reputable publications. Here are some highlights:
                    </p>

                    <ul className={css.list}>
                        <li>
                            <strong>CNET </strong>
                            – "Forget Airbnb - Housesitters get to travel and save on rent" (Sept 2024)
                        </li>
                        <li>
                            <strong>BBC Sounds </strong>
                            – Sarah Walker interview (Feb 2023)
                        </li>
                        <li>
                            <strong>The Sun </strong>
                            – "How’s about that – Living for free in a 5-bedroom house" (Jan 2023)
                        </li>
                        <li>
                            <strong>The Guardian </strong>
                            – "Housesitters escaping the cost-of-living crisis" (Nov 2022)
                        </li>
                        <li>
                            <strong>The Mirror </strong>
                            – "Housesitting, virtual trips, and smart flying" (July 2022)
                        </li>
                        <li>
                            <strong>The Sun </strong>
                            – "Fee holiday in Budapest after nightmare hostel stay" (Dec 2022)
                        </li>
                        <li>
                            <strong>The Independent </strong>
                            – "Is a house swap the affordable answer?" (June 2021)
                        </li>
                    </ul>
                    <p className={css.info}>
                        For full articles or additional media coverage, please reach out to us directly.
                    </p>
                    <p className={css.subtitle}>
                        Awards & Recognition
                    </p>
                    <p className={css.info}>
                        Over the years, HouseSit Match has earned accolades in recognition of our innovative approach to affordable travel and the growing house sitting community:
                    </p>
                    <ul className={css.list}>
                        <li>
                            <strong>2018 </strong>
                            – Nomination for Sue Ryder Southern Women of Achievement
                        </li>
                        <li>
                            <strong>2016 </strong>
                            – The Guardian Small Business Network Award
                        </li>
                    </ul>
                    <H3>
                        Company Background
                    </H3>
                    <p className={css.info}>
                        <strong>HouseSit Match </strong>
                        was founded in 2014 by Lamia Walker, with the goal of creating an online network to connect homeowners with reliable house-sitters. The platform enables homeowners to
                        <NamedLink name="SearchHouseSitterPage"> find trusted sitters </NamedLink>
                        to look after their homes and pets, while sitters gain free accommodation in exchange for their services. This creates a win-win situation for both parties, helping homeowners travel with peace of mind while providing sitters with affordable options for travel and accommodation.
                    </p>
                    <p className={css.subtitle}>
                        About Lamia Walker
                    </p>
                    <p className={css.info}>
                        Lamia Walker is the founder and visionary behind HouseSit Match. With more than 25 years of experience in marketing and entrepreneurship, Lamia’s journey began when a friend shared her successful experience of house-sitting in Australia, where she saved significant money by not paying rent. Inspired by this model, Lamia left her senior publishing role to pursue the creation of HouseSit Match, which launched in 2014.
                    </p>
                    <p className={css.info}>
                        Today, HouseSit Match has grown into a
                        <NamedLink name="AboutHouseSitMatchPage"> global platform</NamedLink>
                        , offering thousands of house sitting opportunities and helping people experience affordable travel, while homeowners find trusted sitters.
                    </p>
                    <p className={css.subtitle}>
                        Press Inquiries
                    </p>
                    <p className={css.info}>
                        For media inquiries, interview requests, or to learn more about HouseSit Match, please contact us:
                    </p>

                    <ul className={css.list}>
                        <li>
                            <strong>
                                Email:
                            </strong>
                            [<a href="mailto: lamia@housesitmatch.com">lamia@housesitmatch.com</a>]
                        </li>
                    </ul>
                    <p className={css.subtitle}>
                        Press Kit
                    </p>
                    <p className={css.info}>
                        Download our Press Kit for logos, high-resolution photos, and detailed company information:
                    </p>
                    <p className={css.info}>
                        [Download Press Kit] – Link to downloadable content (e.g., logos, photos, PDF)
                    </p>
                    <p className={css.subtitle}>
                        Past Events & Presentations
                    </p>
                    <p className={css.info}>
                        HouseSit Match has been involved in several notable events and presentations:
                    </p>
                    <ul className={css.list}>
                        <li>
                            <strong>
                                2017 {" "}
                            </strong>
                            – Digital Gum Workshop – Starting a Business, Green Park, Reading
                        </li>
                        <li>
                            <strong>
                                2016{" "}
                            </strong>
                            – World Travel Market – Women in Travel
                        </li>
                        <li>
                            <strong>
                                2015{" "}
                            </strong>
                            – World Travel Market – Women in Travel
                        </li>
                    </ul>
                    <p className={css.subtitle}>
                        Media Features
                    </p>
                    <p className={css.info}>
                        Over the years, HouseSit Match has been featured in several well-known publications, including:
                    </p>

                    <ul className={css.list}>
                        <li>
                            <strong>
                                Huffington Post{" "}
                            </strong>
                            – "How to Travel Long in Retirement" (June 2017)
                        </li>
                        <li>
                            <strong>
                                House Beautiful{" "}
                            </strong>
                            – "5 Things to Look for in a Potential Housesitter" (Jan 2017)
                        </li>
                        <li>
                            <strong>
                                The Telegraph{" "}
                            </strong>
                            – "Rental Prices Worldwide – How to Find the Cheapest Properties" (Dec 2015)
                        </li>
                        <li>
                            <strong>
                                Forbes Magazine{" "}
                            </strong>
                            – "A Fun Way to Travel the World on a Dime" (Sept 2014)
                        </li>
                        <li>
                            <strong>
                                French Property News{" "}
                            </strong>
                            – HouseSit Match named the Website of the Month (May 2014)
                        </li>
                    </ul>
                </div>
            </LayoutSingleColumn>
        </Page >
    );
};

export default PressRoomPage;